import { useInviteLinksStore } from '@/features/analytics/model'
import {
    useDeleteInviteLinkMutation,
    useGetGroupsByChannelIdQuery,
    useMoveInviteLinkMutation,
} from '@/shared/api'
import { GearSVGIcon, TrashIcon } from '@/shared/assets'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuPortal,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@/shared/ui'
import { Check, ChevronRight, XIcon } from 'lucide-react'
import { forwardRef, useCallback, useMemo, useState } from 'react'

type DropdownMenuDemoProps = {
    channelId: number
    groupId: number
}

export function DropdownMenuDemo({
    groupId,
    channelId,
}: DropdownMenuDemoProps) {
    const { clearSelectedLinksInviteByGroup, selectedLinksInviteByGroup } =
        useInviteLinksStore()
    const selectedLinks = useMemo(
        () => selectedLinksInviteByGroup[groupId] || [],
        [groupId, selectedLinksInviteByGroup]
    )

    const { mutate } = useDeleteInviteLinkMutation({
        channelId,
    })

    const handleDeselect = useCallback(() => {
        clearSelectedLinksInviteByGroup(groupId)
    }, [clearSelectedLinksInviteByGroup, groupId])

    const handleDelete = useCallback(() => {
        for (let i = 0; i < selectedLinks.length; i++) {
            mutate(selectedLinks[i].invite_link_id)
        }
        setShowDeleteConfirmation(false)
        handleDeselect()
    }, [handleDeselect, mutate, selectedLinks])

    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

    const handleDeleteConfirmation = (event: React.MouseEvent) => {
        if (!showDeleteConfirmation) {
            setShowDeleteConfirmation(true)
            event.preventDefault()
        }
    }

    const handleCancelDelete = (event: React.MouseEvent) => {
        event.stopPropagation()
        setShowDeleteConfirmation(false)
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <div className="cursor-pointer">
                    <GearSVGIcon className="h-[17px] w-[17px]" />
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                side="right"
                align="start"
                className="w-[190px] border-[#E9E9ED] bg-white"
            >
                <DropdownMenuGroup className="gap-2 rounded-xl p-1">
                    <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="cursor-pointer rounded-xl p-2 text-[13px] hover:bg-[#F2F6FD]">
                            <span>Перенести в группу</span>
                        </DropdownMenuSubTrigger>
                        <DropdownMenuPortal>
                            <MoveToGroupConfirmation
                                groupId={groupId}
                                channelId={channelId}
                            />
                        </DropdownMenuPortal>
                    </DropdownMenuSub>
                    <DropdownMenuItem
                        onClick={handleDeselect}
                        className="cursor-pointer rounded-xl p-2 text-[13px] hover:bg-[#F2F6FD]"
                    >
                        <span>Снять выделение</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClickCapture={handleDeleteConfirmation}
                        className="cursor-pointer justify-between rounded-xl p-2 text-[13px] hover:bg-[#F2F6FD]"
                    >
                        <span>Удалить</span>
                        {showDeleteConfirmation ? (
                            <div className="flex justify-between">
                                <div onClick={handleDelete}>
                                    <Check className="ml-2 cursor-pointer text-[#056FFF]" />
                                </div>
                                <div onClick={handleCancelDelete}>
                                    <XIcon className="ml-2 cursor-pointer" />
                                </div>
                            </div>
                        ) : (
                            <img src={TrashIcon} />
                        )}
                    </DropdownMenuItem>
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

type MoveToGroupConfirmationProps = {
    groupId: number
    channelId: number
}
const MoveToGroupConfirmation = forwardRef<
    HTMLDivElement,
    MoveToGroupConfirmationProps
>(({ groupId, channelId }, ref) => {
    const { data: allGroups = [] } = useGetGroupsByChannelIdQuery(channelId)

    const {
        selectedLinksInviteByGroup,
        clearSelectedLinksInviteByGroup,
        addMovedInviteLinksToGroup,
        removeMovedInviteLinksFromGroup,
    } = useInviteLinksStore()

    const [confirmGroupId, setConfirmGroupId] = useState<number | null>(null)

    const handleGroupClick = (groupId: number) => {
        setConfirmGroupId(groupId)
    }

    const moveInviteLinksMutation = useMoveInviteLinkMutation({
        onSuccess: () => {
            clearSelectedLinksInviteByGroup(groupId)
        },
    })

    const handleConfirm = () => {
        const selectedIds = selectedLinksInviteByGroup[groupId].map(
            (link) => link.invite_link_id
        )
        if (confirmGroupId !== null && selectedIds.length > 0) {
            removeMovedInviteLinksFromGroup(
                groupId,
                selectedLinksInviteByGroup[groupId]
            )
            addMovedInviteLinksToGroup(
                confirmGroupId,
                selectedLinksInviteByGroup[groupId]
            )
            moveInviteLinksMutation.mutate(
                {
                    targetGroupId: confirmGroupId,
                    sourceGroupId: groupId,
                    inviteLinkIds: selectedIds,
                },
                {
                    onError: () => {
                        // Revert the changes if the backend call fails
                        addMovedInviteLinksToGroup(
                            groupId,
                            selectedLinksInviteByGroup[groupId]
                        )
                        removeMovedInviteLinksFromGroup(
                            confirmGroupId,
                            selectedLinksInviteByGroup[groupId]
                        )
                    },
                }
            )
        }
        setConfirmGroupId(null)
    }

    const handleCancel = () => {
        setConfirmGroupId(null)
    }

    const groups = allGroups.filter(
        (group) =>
            group.group_name != 'Предыдущие инвайт линки' && // TODO: fix hardcoded group name
            group.invite_links_group_id !== groupId
    )

    if (groups.length === 0) {
        return null
    }

    return (
        <DropdownMenuSubContent className="bg-white" ref={ref}>
            {groups.map((group) => (
                <div key={group.invite_links_group_id}>
                    <DropdownMenuItem
                        key={group.invite_links_group_id}
                        className="cursor-pointer justify-between rounded-xl p-2 text-[13px] hover:bg-[#F2F6FD]"
                        onClickCapture={(event) => {
                            if (
                                confirmGroupId !== group.invite_links_group_id
                            ) {
                                handleGroupClick(group.invite_links_group_id)
                                event.preventDefault()
                            }
                        }}
                    >
                        <span>{group.group_name}</span>

                        {confirmGroupId === group.invite_links_group_id ? (
                            <div className="flex items-center">
                                <div
                                    onClick={() => {
                                        handleConfirm()
                                    }}
                                >
                                    <Check className="ml-2 cursor-pointer text-[#056FFF]" />
                                </div>
                                <div
                                    onClick={() => {
                                        handleCancel()
                                    }}
                                >
                                    <XIcon className="ml-2 cursor-pointer" />
                                </div>
                            </div>
                        ) : (
                            <ChevronRight className="ml-auto" />
                        )}
                    </DropdownMenuItem>
                </div>
            ))}
        </DropdownMenuSubContent>
    )
})
